import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card } from '@totalsoft/rocket-ui';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { DICTIONARY_ITEMS_QUERY, GENERIC_DICTIONARY_ITEMS_QUERY } from '../queries';
import ListBrowser, { getListBrowserStore, useListBrowserUtils } from '@ts-private/list-browser';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import { sortBy } from 'utils/functions';
import DictionaryDetailListContainer from './DictionaryDetailListContainer';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';

export const dictionaryDetailsStore = getListBrowserStore({
  orderByFields: [
    {
      caption: 'Dictionaries.DictionaryItem.Code',
      value: 'code'
    },
    {
      caption: 'Dictionaries.DictionaryItem.Name',
      value: 'name'
    },
    {
      caption: 'Dictionaries.DictionaryItem.OrderNo',
      value: 'orderNo'
    }
  ],
  pager: {
    orderBy: 'code',
    direction: 'asc'
  },
  withQueryFilters: false
});

const filterDictionaryDetails = (data, pager, filters) => {
  let details = [...data];

  if (filters.search)
    details = details.filter(detail => {
      if (detail.code && detail.code.toLowerCase().trim().includes(filters.search.toLowerCase().trim())) return true;
      if (detail.name && detail.name.toLowerCase().trim().includes(filters.search.toLowerCase().trim())) return true;
      if (detail.description && detail.description.toLowerCase().trim().includes(filters.search.toLowerCase().trim())) return true;
      return false;
    });

  const orderBy = [];
  if (pager.orderBy) orderBy.push(pager.orderBy);
  else orderBy.push('code', 'name');
  sortBy(details, orderBy, pager.direction);

  return details;
};

const DictionaryDetailListBrowser = ({ dictionary }) => {
  const withDictionaryItem = useMemo(() => Boolean(dictionary.dictionaryId), [dictionary.dictionaryId]);

  const fetchingOptions = useMemo(() => {
    const query = withDictionaryItem ? DICTIONARY_ITEMS_QUERY : GENERIC_DICTIONARY_ITEMS_QUERY;
    const name = withDictionaryItem ? 'dictionaryItems' : 'genericDictionaryItems';
    const variables = { dictionaryId: withDictionaryItem ? dictionary.dictionaryId : dictionary.id };

    return {
      queryOptions: {
        query,
        name,
        variables,
        fetchPolicy: 'network-only'
      },
      clientFilter: filterDictionaryDetails
    };
  }, [dictionary, withDictionaryItem]);

  const itemsComponent = useCallback(
    () => <DictionaryDetailListContainer dictionary={dictionary} withDictionaryItem={withDictionaryItem} />,
    [dictionary, withDictionaryItem]
  );

  return (
    <Card icon={ListAltIcon}>
      <ListBrowser name='dictionary-details-browser' store={dictionaryDetailsStore} ItemsComponent={itemsComponent} />
      <ApolloFetchingComponent store={dictionaryDetailsStore} options={fetchingOptions} />
      <ExportModalComponent store={dictionaryDetailsStore} options={fetchingOptions} />
      <DictionaryDetailsStoreHandler withDictionaryItem={withDictionaryItem} />
    </Card>
  );
};

DictionaryDetailListBrowser.propTypes = {
  dictionary: PropTypes.object
};

export default DictionaryDetailListBrowser;

const DictionaryDetailsStoreHandler = ({ withDictionaryItem }) => {
  const { update } = useListBrowserUtils(dictionaryDetailsStore);

  useEffect(() => {
    update(prev => ({
      ...prev,
      orderByFields: prev.orderByFields.map(obf => (obf.value === 'orderNo' ? { ...obf, hidden: withDictionaryItem } : obf))
    }));
  }, [withDictionaryItem, update]);

  return null;
};

DictionaryDetailsStoreHandler.propTypes = {
  withDictionaryItem: PropTypes.bool
};
